import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Shield,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  RefreshCw,
  CloudUpload,
  Plus,
  ListChecks,
  Pencil,
  Trash2,
} from 'lucide-react';
import { getApiBase } from './apiBase';
import { downloadCsv } from './AiCopilot/downloadCsv';
const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 120000,
});

/** Paste this verbatim into Generate → Natural language question */
export const UAR_DEFAULT_GENERATE_QUESTION =
  "Create an access review campaign for all users who hold 'Compensation Manager' or 'Payroll' roles in the US legislation";

function defaultCampaignName() {
  return `US Compensation Manager and Payroll - ${new Date().toISOString().slice(0, 10)}`;
}

function StatusBadge({ status }) {
  const map = {
    Pending: 'bg-amber-100 text-amber-800 border-amber-200',
    Approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Revoked: 'bg-rose-100 text-rose-800 border-rose-200',
  };
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${
        map[status] || 'bg-slate-100 text-slate-700 border-slate-200'
      }`}
    >
      {status}
    </span>
  );
}

function CampaignList({ campaigns, loading, onSelect, onRefresh, onEdit, onDelete }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading campaigns…
      </div>
    );
  }
  if (!campaigns.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
        <ListChecks className="h-8 w-8 mx-auto mb-2 text-slate-400" />
        No campaigns yet.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 bg-slate-50">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Campaigns</span>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Items</th>
            <th className="px-4 py-2 font-medium">Pending</th>
            <th className="px-4 py-2 font-medium">Revoked</th>
            <th className="px-4 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => (
            <tr
              key={c.id}
              className="border-b border-slate-100 hover:bg-cyan-50/50 cursor-pointer"
              onClick={() => onSelect(c.id)}
            >
              <td className="px-4 py-3 font-medium text-slate-900">{c.campaignName}</td>
              <td className="px-4 py-3">
                <StatusBadge status={c.status} />
              </td>
              <td className="px-4 py-3 text-slate-600">{c.lineItemCount}</td>
              <td className="px-4 py-3 text-amber-700">{c.pendingCount}</td>
              <td className="px-4 py-3 text-rose-700">{c.revokedCount}</td>
              <td className="px-4 py-3 text-right">
                <div
                  className="inline-flex gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => onEdit(c)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                    title="Edit campaign"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(c)}
                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                    title="Delete campaign"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CampaignDetail({
  campaign,
  loading,
  error,
  actionLoading,
  remediateResult,
  onBack,
  onLineItem,
  onRemediate,
}) {
  function exportReminderCsv() {
    const items = campaign?.lineItems || [];
    const rows = items.map((li) => ({
      username: li.username,
      workerName: li.workerName,
      personNumber: li.personNumber,
      roleName: li.roleName,
      status: li.status,
    }));
    downloadCsv(`uar-reminder-${campaign.campaignName || 'campaign'}.csv`, Object.keys(rows[0] || {}), rows);
  }

  function copyReviewerEmails() {
    const emails = (campaign?.reviewers || [])
      .map((r) => r.managerEmail)
      .filter(Boolean);
    const text = emails.length ? emails.join('; ') : '(no reviewer emails on campaign — add reviewers to enable copy)';
    if (navigator?.clipboard) navigator.clipboard.writeText(text);
  }
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading campaign…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 flex gap-2">
        <AlertCircle className="h-5 w-5 shrink-0" />
        {error}
      </div>
    );
  }
  if (!campaign) return null;

  const items = campaign.lineItems || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          <ChevronLeft className="h-4 w-4" />
          All campaigns
        </button>
        <h2 className="text-lg font-semibold text-slate-900 flex-1">{campaign.campaignName}</h2>
        <button
          type="button"
          onClick={onRemediate}
          disabled={actionLoading}
          className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
        >
          {actionLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CloudUpload className="h-4 w-4" />
          )}
          Submit &amp; Sync to Oracle
        </button>
        <button
          type="button"
          onClick={exportReminderCsv}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
        >
          Export reminder CSV
        </button>
        <button
          type="button"
          onClick={copyReviewerEmails}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
        >
          Copy email list
        </button>
      </div>

      {campaign.targetRole && (
        <p className="text-sm text-slate-600">
          Target role filter: <span className="font-medium">{campaign.targetRole}</span>
        </p>
      )}

      {remediateResult && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            remediateResult.failed > 0
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-emerald-200 bg-emerald-50 text-emerald-900'
          }`}
        >
          <p className="font-medium">
            Remediation: {remediateResult.succeeded} succeeded, {remediateResult.failed} failed
          </p>
          {(remediateResult.errors || []).slice(0, 5).map((e, i) => (
            <p key={i} className="mt-1 text-xs">
              {e.username} / {e.roleName}: {e.message}
            </p>
          ))}
        </div>
      )}

      {(campaign.remediationLog || []).length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
          <summary className="cursor-pointer font-semibold text-slate-800">Remediation audit log</summary>
          <ul className="mt-2 space-y-1">
            {campaign.remediationLog.map((e, i) => (
              <li key={i}>
                {e.at ? new Date(e.at).toLocaleString() : '—'} · {e.performedBy || 'system'} · revoked{' '}
                {e.revokedCount} · ok {e.successCount} · err {e.errorCount}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500">
              <th className="px-4 py-2 font-medium">User</th>
              <th className="px-4 py-2 font-medium">Person #</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((li, idx) => {
              const processed = li.status !== 'Pending';
              return (
                <tr
                  key={li._id || `${li.userId}-${li.roleName}-${idx}`}
                  className={`border-b border-slate-100 ${processed ? 'bg-slate-50/80' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div className={processed ? 'line-through text-slate-500' : 'font-medium text-slate-900'}>
                      {li.username || li.userId}
                    </div>
                    {li.workerName && (
                      <div className="text-xs text-slate-500">{li.workerName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{li.personNumber || '—'}</td>
                  <td className={`px-4 py-3 ${processed ? 'line-through text-slate-500' : ''}`}>
                    {li.roleName}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={li.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {li.status === 'Pending' ? (
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={() => onLineItem(idx, 'Approved')}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={() => onLineItem(idx, 'Revoked')}
                          className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-rose-500 disabled:opacity-50"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Revoke
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">
                        {li.reviewedBy ? `by ${li.reviewedBy}` : 'Reviewed'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function UserAccessReview() {
  const [campaigns, setCampaigns] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [remediateResult, setRemediateResult] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createTargetRole, setCreateTargetRole] = useState('');
  const [createQuestion, setCreateQuestion] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [previewLineItems, setPreviewLineItems] = useState(null);

  const [editCampaign, setEditCampaign] = useState(null);
  const [editName, setEditName] = useState('');
  const [editTargetRole, setEditTargetRole] = useState('');
  const [editStatus, setEditStatus] = useState('Active');
  const [editQuestion, setEditQuestion] = useState('');
  const [editPreviewLineItems, setEditPreviewLineItems] = useState(null);
  const [uarTemplates, setUarTemplates] = useState([]);

  useEffect(() => {
    API.get('/api/security/uar/templates', { validateStatus: () => true }).then((res) => {
      if (res.status === 200) setUarTemplates(res.data.templates || []);
    });
  }, []);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const res = await API.get('/api/security/campaigns', { validateStatus: () => true });
      if (res.status === 200) {
        setCampaigns(res.data.campaigns || []);
      } else {
        setListError(res.data?.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setListError(err.message || 'Failed to load campaigns');
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    setDetailError('');
    setRemediateResult(null);
    try {
      const res = await API.get(`/api/security/campaigns/${id}`, { validateStatus: () => true });
      if (res.status === 200) {
        setCampaign(res.data.campaign);
      } else {
        setDetailError(res.data?.error || `HTTP ${res.status}`);
        setCampaign(null);
      }
    } catch (err) {
      setDetailError(err.message || 'Failed to load campaign');
      setCampaign(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setCampaign(null);
  }, [selectedId, loadDetail]);

  async function handleLineItem(lineItemIndex, status) {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      const res = await API.put(
        `/api/security/campaigns/${selectedId}/lineitem`,
        { lineItemIndex, status },
        { validateStatus: () => true }
      );
      if (res.status === 200) {
        setCampaign(res.data.campaign);
        loadList();
      } else {
        setDetailError(res.data?.error || `Update failed (${res.status})`);
      }
    } catch (err) {
      setDetailError(err.message || 'Update failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemediate() {
    if (!selectedId) return;
    setActionLoading(true);
    setRemediateResult(null);
    try {
      const res = await API.post(
        `/api/security/campaigns/${selectedId}/remediate`,
        {},
        { validateStatus: () => true }
      );
      if (res.status === 200) {
        setRemediateResult(res.data);
      } else {
        setDetailError(res.data?.error || `Remediation failed (${res.status})`);
      }
    } catch (err) {
      setDetailError(err.message || 'Remediation failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleGeneratePreview() {
    setCreateLoading(true);
    setCreateError('');
    setPreviewLineItems(null);
    try {
      const res = await API.post(
        '/api/security/campaigns/generate-from-query',
        {
          question: createQuestion.trim() || undefined,
          targetRole: createTargetRole.trim() || undefined,
        },
        { validateStatus: () => true }
      );
      if (res.status === 200) {
        setPreviewLineItems(res.data.lineItems || []);
        if (res.data.suggestedCampaignName && !createName.trim()) {
          setCreateName(res.data.suggestedCampaignName);
        }
      } else {
        setCreateError(res.data?.error || `Generate failed (${res.status})`);
      }
    } catch (err) {
      setCreateError(err.message || 'Generate failed');
    } finally {
      setCreateLoading(false);
    }
  }

  function openEdit(c) {
    setEditCampaign(c);
    setEditName(c.campaignName || '');
    setEditTargetRole(c.targetRole || '');
    setEditStatus(c.status || 'Active');
    setEditQuestion('');
    setEditPreviewLineItems(null);
    setEditError('');
  }

  function closeEdit() {
    setEditCampaign(null);
    setEditError('');
    setEditPreviewLineItems(null);
  }

  async function handleDeleteCampaign(c) {
    if (!window.confirm(`Delete campaign "${c.campaignName}"? This cannot be undone.`)) return;
    setListError('');
    try {
      const res = await API.delete(`/api/security/campaigns/${c.id}`, { validateStatus: () => true });
      if (res.status === 200) {
        if (selectedId === c.id) setSelectedId(null);
        await loadList();
      } else {
        setListError(res.data?.error || `Delete failed (${res.status})`);
      }
    } catch (err) {
      setListError(err.message || 'Delete failed');
    }
  }

  async function handleEditGeneratePreview() {
    setEditLoading(true);
    setEditError('');
    setEditPreviewLineItems(null);
    try {
      const res = await API.post(
        '/api/security/campaigns/generate-from-query',
        {
          question: editQuestion.trim() || undefined,
          targetRole: editTargetRole.trim() || undefined,
        },
        { validateStatus: () => true }
      );
      if (res.status === 200) {
        setEditPreviewLineItems(res.data.lineItems || []);
      } else {
        setEditError(res.data?.error || `Generate failed (${res.status})`);
      }
    } catch (err) {
      setEditError(err.message || 'Generate failed');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleSaveEdit() {
    if (!editCampaign) return;
    const name = editName.trim();
    if (!name) {
      setEditError('Campaign name is required');
      return;
    }
    setEditLoading(true);
    setEditError('');
    try {
      const body = {
        campaignName: name,
        targetRole: editTargetRole.trim(),
        status: editStatus,
      };
      if (editPreviewLineItems !== null) {
        body.lineItems = editPreviewLineItems;
      }
      const res = await API.put(`/api/security/campaigns/${editCampaign.id}`, body, {
        validateStatus: () => true,
      });
      if (res.status === 200) {
        closeEdit();
        await loadList();
        if (selectedId === editCampaign.id) loadDetail(editCampaign.id);
      } else {
        setEditError(res.data?.error || `Update failed (${res.status})`);
      }
    } catch (err) {
      setEditError(err.message || 'Update failed');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleCreateCampaign() {
    const name = createName.trim();
    if (!name) {
      setCreateError('Campaign name is required');
      return;
    }
    const items = previewLineItems || [];
    setCreateLoading(true);
    setCreateError('');
    try {
      const res = await API.post(
        '/api/security/campaigns/create',
        {
          campaignName: name,
          targetRole: createTargetRole.trim() || undefined,
          lineItems: items,
        },
        { validateStatus: () => true }
      );
      if (res.status === 201) {
        setShowCreate(false);
        setCreateName('');
        setCreateQuestion('');
        setPreviewLineItems(null);
        await loadList();
        setSelectedId(res.data.campaign.id);
      } else {
        setCreateError(res.data?.error || `Create failed (${res.status})`);
      }
    } catch (err) {
      setCreateError(err.message || 'Create failed');
    } finally {
      setCreateLoading(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex flex-wrap items-start gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 text-white shadow">
          <Shield className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">Access Review (UAR)</h1>
        </div>
        {!selectedId && (
          <button
            type="button"
            onClick={() => {
              setShowCreate((v) => {
                const next = !v;
                if (next) {
                  if (!createQuestion.trim()) setCreateQuestion(UAR_DEFAULT_GENERATE_QUESTION);
                  if (!createName.trim()) setCreateName(defaultCampaignName());
                }
                return next;
              });
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-cyan-700 bg-white px-4 py-2 text-sm font-medium text-cyan-800 hover:bg-cyan-50"
          >
            <Plus className="h-4 w-4" />
            New campaign
          </button>
        )}
      </header>

      {listError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex gap-2">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {listError}
        </div>
      )}

      {editCampaign && !selectedId && (
        <div
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3"
          role="dialog"
          aria-labelledby="edit-campaign-title"
        >
          <div className="flex items-center justify-between gap-2">
            <h3 id="edit-campaign-title" className="font-semibold text-slate-900">
              Edit campaign
            </h3>
            <button
              type="button"
              onClick={closeEdit}
              className="text-xs text-slate-500 hover:text-slate-800"
            >
              Cancel
            </button>
          </div>
          <input
            type="text"
            placeholder="Campaign name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Target role filter (optional)"
            value={editTargetRole}
            onChange={(e) => setEditTargetRole(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <label className="block text-xs font-medium text-slate-600">
            Campaign status
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
            </select>
          </label>
          <textarea
            placeholder="Optional: natural language question to regenerate line items"
            value={editQuestion}
            onChange={(e) => setEditQuestion(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono text-[13px]"
          />
          {editError && <p className="text-sm text-rose-600">{editError}</p>}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleEditGeneratePreview}
              disabled={
                editLoading || (!editQuestion.trim() && !editTargetRole.trim())
              }
              className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-600 disabled:opacity-50"
            >
              {editLoading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null}
              Regenerate line items
            </button>
            {editPreviewLineItems !== null && (
              <span className="text-sm text-slate-600 self-center">
                {editPreviewLineItems.length} rows ready to apply on save
              </span>
            )}
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={editLoading || !editName.trim()}
              className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
            >
              Save changes
            </button>
          </div>
        </div>
      )}

      {showCreate && !selectedId && !editCampaign && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
          <h3 className="font-semibold text-slate-900">Create campaign</h3>
          {uarTemplates.length > 0 && (
            <label className="block text-xs font-medium text-slate-600">
              Campaign template
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                defaultValue=""
                onChange={(e) => {
                  const t = uarTemplates.find((x) => x.id === e.target.value);
                  if (!t) return;
                  setCreateName(t.name);
                  setCreateTargetRole(t.targetRole || '');
                  if (t.generateHint) setCreateQuestion(t.generateHint);
                }}
              >
                <option value="">— optional preset —</option>
                {uarTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <input
            type="text"
            placeholder="Campaign name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Target role filter (optional)"
            value={createTargetRole}
            onChange={(e) => setCreateTargetRole(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <textarea
            placeholder={UAR_DEFAULT_GENERATE_QUESTION}
            value={createQuestion}
            onChange={(e) => setCreateQuestion(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono text-[13px]"
          />
          {createError && <p className="text-sm text-rose-600">{createError}</p>}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleGeneratePreview}
              disabled={createLoading || (!createQuestion.trim() && !createTargetRole.trim())}
              className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-600 disabled:opacity-50"
            >
              {createLoading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null}
              Generate line items
            </button>
            {previewLineItems !== null && (
              <span className="text-sm text-slate-600 self-center">
                {previewLineItems.length} rows ready
                {previewLineItems.length === 0
                  ? ' — adjust filters and regenerate, or save an empty draft'
                  : ''}
              </span>
            )}
            <button
              type="button"
              onClick={handleCreateCampaign}
              disabled={createLoading || !createName.trim()}
              className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
            >
              Save campaign
            </button>
          </div>
        </div>
      )}

      {selectedId ? (
        <CampaignDetail
          campaign={campaign}
          loading={detailLoading}
          error={detailError}
          actionLoading={actionLoading}
          remediateResult={remediateResult}
          onBack={() => {
            setSelectedId(null);
            setDetailError('');
            setRemediateResult(null);
          }}
          onLineItem={handleLineItem}
          onRemediate={handleRemediate}
        />
      ) : (
        <>
          <CampaignList
            campaigns={campaigns}
            loading={listLoading}
            onSelect={setSelectedId}
            onRefresh={loadList}
            onEdit={openEdit}
            onDelete={handleDeleteCampaign}
          />
        </>
      )}
    </div>
  );
}
